import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    input,
    InputSignal,
    OnInit,
    Output,
    QueryList,
    signal,
    ViewChildren
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LanguageModule, SharedLanguageService } from '@language';
import { Observable, take } from 'rxjs';
import { FocusManagerService } from '../../../core/common/services/focus-manager.service';
import { ConfirmationDialogConfig, DialogHelperBase } from '../../../dynamic-dialog/helpers/dialog.helper';
import { FormTagListStyling } from '../../../form-controls/entities/form-tag-list-style.entity';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { TagListLabels } from '../../../form-controls/entities/tag-list-tooltips.entity';
import { mod } from '../../../helpers/modulo';
import { ShAutoFocusDirective } from '../../directives/auto-focus/auto-focus.directive';
import { ShClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { ShDynamicResizeDirective } from '../../directives/dynamic-resize/dynamic-resize.directive';
import { IconSize, ShIconComponent } from '../icon/icon.module';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'sh-tag-list',
    templateUrl: './tag-list.component.html',
    styleUrls: ['./tag-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        ShIconComponent,
        ShDynamicResizeDirective,
        LanguageModule,
        ShAutoFocusDirective,
        ShClickOutsideDirective
    ]
})
export class ShTagListComponent implements OnInit {
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);
    private readonly focusManager: FocusManagerService = inject(FocusManagerService);
    private readonly dialogHelper: DialogHelperBase = inject(DialogHelperBase);

    protected readonly componentId: string = `${this.constructor.name}-${Math.random().toString(36).slice(0, 9)}`;

    public sizes: typeof IconSize = IconSize;
    public tagFocusIndex: number = -1;

    public tagEditFormControl: FormControl<string | null> = new FormControl();
    public currentTagEditIndex = signal(-1);

    public _tagIdPrefix: string;
    public _labels: TagListLabels = {
        confirmTooltip: 'ui.confirm',
        editTooltip: 'tags.edit',
        editWarningMessage: 'tags.edit-warning-message',
        editWarningTitle: 'tags.edit',
        removeTooltip: 'tags.remove',
        removeWarningMessage: 'tags.remove-warning-message',
        removeWarningTitle: 'tags.remove'
    };

    // #region Inputs
    tags = input.required<FormValue[]>();
    disabled = input<boolean>(false);
    editable = input<boolean>(false);
    singleTag = input<boolean>(false);
    limitedButtons: InputSignal<boolean> = input(false);

    @Input() id: string;
    @Input() tagIdPrefix: string;
    @Input() styling: FormTagListStyling = {
        style: 'Default',
        modifiers: [],
        truncate: true
    };

    @Input() set labels(labels: Partial<TagListLabels>) {
        this._labels = { ...this._labels, ...labels };
    }

    @Input() deleteWarning: boolean = false;
    @Input() editWarning: boolean = false;

    @Output() onTagsUpdate: EventEmitter<FormValue[]> = new EventEmitter<FormValue[]>();
    @Output() onTagRemove: EventEmitter<string> = new EventEmitter<string>();
    @Output() onTagFocus: EventEmitter<string | null> = new EventEmitter<string | null>();

    @ViewChildren('tagItem') tagItems!: QueryList<ElementRef>;
    // #endregion

    // #region HostListeners
    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (!this.isActiveComponent()) return;

        if (event.key === 'ArrowLeft' && this.tagFocusIndex >= 0 && this.currentTagEditIndex() < 0) {
            event.preventDefault();
            this.tagFocusIndex = mod(this.tagFocusIndex - 1, this.tagItems.toArray().length);
        }

        if (event.key === 'ArrowRight' && this.tagFocusIndex >= 0 && this.currentTagEditIndex() < 0) {
            event.preventDefault();
            this.tagFocusIndex = mod(this.tagFocusIndex + 1, this.tagItems.toArray().length);
        }

        if (event.key === 'Tab' || event.key === 'Escape') {
            this.deactivate();
        }
    }

    // #endregion

    // #region Lifecycle hooks
    ngOnInit(): void {
        this._tagIdPrefix = this.tagIdPrefix ?? `${this.id}-tag-list-tag'`;
    }

    ngAfterViewChecked() {
        if (this.isActiveComponent()) {
            this.focusTags();
        }
    }
    // #endregion

    // #region Interactions
    public onEscape(): void {
        this.closeEditMode();
    }

    public onFocus(tagId: string | null): void {
        if (this.tagFocusIndex === -1) {
            this.tagFocusIndex = 0;
        }
        this.activate();
        this.onTagFocus.emit(tagId);
    }

    public onFocusOut(): void {
        this.deactivate();
        this.onTagFocus.emit(null);
    }
    // #endregion

    // #region Edit tag
    public onEditMode(index: number, initialValue: string, event?: MouseEvent): void {
        if (this.disabled() || !this.editable()) {
            return;
        }

        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        this.currentTagEditIndex.set(index);
        this.tagFocusIndex = index;
        this.tagEditFormControl.setValue(initialValue);
    }

    public onSubmitTagEditChange(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        if (this.currentTagEditIndex() < 0) {
            return;
        }

        // Do nothing if value has not changed or was completely cleared
        if (!this.hasValueChanged() || this.hasEmptyValue()) {
            this.closeEditMode();
            return;
        }

        if (!this.editWarning) {
            this.emitTagChange();
        } else {
            this.openWarningDialog(
                this.translateService.translate(this._labels.editWarningTitle),
                this.translateService.translate(this._labels.editWarningMessage)
            )
                .pipe(
                    finalize(() => {
                        this.closeEditMode();
                    }),
                    take(1)
                )
                .subscribe(() => {
                    this.emitTagChange();
                });
        }
    }

    private emitTagChange(): void {
        let newState: FormValue[] = [...this.tags()];

        if (this.tagEditFormControl.value) {
            const oldValue: FormValue = this.tags()[this.currentTagEditIndex()];
            const newValue: string = this.tagEditFormControl.value;

            newState = this.updateStateOnEdit(newState, oldValue, newValue);
            this.onTagsUpdate.emit(newState);
        } else {
            // All characters were deleted, remove tag from state
            const oldValue: FormValue = this.tags()[this.currentTagEditIndex()];

            this.removeTag(oldValue.value);
        }
        this.closeEditMode();
    }

    private hasValueChanged(): boolean {
        const oldValue: FormValue = this.tags()[this.currentTagEditIndex()];
        const newValue: string = this.tagEditFormControl.value ?? '';

        // No changes were made, do nothing
        return oldValue.value !== newValue;
    }

    private hasEmptyValue(): boolean {
        return this.tagEditFormControl.value?.trim().length === 0;
    }

    private updateStateOnEdit(oldState: FormValue[], oldValue: FormValue, newValue: string): FormValue[] {
        const newState: FormValue[] = oldState;

        const indexOfUnset: number = this.findIndexOfUnsetValue(newState, newValue);
        // By editing, the user has restored the original state.
        if (indexOfUnset !== -1) {
            newState.splice(indexOfUnset, 1);
        }

        // Old value is an original value. UNSET it.
        else if (this.tags().find(formValue => formValue.value === oldValue?.value)) {
            newState.push(new FormValue(oldValue.value, FormValueState.UNSET));
        }

        newState[this.currentTagEditIndex()] = new FormValue(newValue, newState[this.currentTagEditIndex()].state);

        return newState;
    }

    private closeEditMode(): void {
        this.currentTagEditIndex.set(-1);
    }
    // #endregion

    // #region Remove tag
    public removeTag(value: string, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        if (this.deleteWarning) {
            this.openWarningDialog(
                this.translateService.translate(this._labels.removeWarningTitle),
                this.translateService.translate(this._labels.removeWarningMessage)
            )
                .pipe(take(1))
                .subscribe(confirmed => confirmed && this.onTagRemove.emit(value));
        } else {
            this.onTagRemove.emit(value);
        }
    }
    // #endregion

    // #region Dialog
    private openWarningDialog(title: string, message: string): Observable<boolean> {
        const config: ConfirmationDialogConfig = {
            title,
            message,
            yes: this.translateService.translate('ui.confirm'),
            no: this.translateService.translate('ui.cancel')
        };
        return this.dialogHelper.confirm(config);
    }
    // #endregion

    // #region Helpers
    private findIndexOfUnsetValue(formValues: FormValue[], value: string): number {
        return formValues.findIndex(formValue => formValue.value === value && formValue.state === FormValueState.UNSET);
    }
    // #endregion

    // #region Focus management
    protected activate() {
        this.focusManager.setActive(this.componentId);
    }

    protected deactivate() {
        this.focusManager.clearActive();
    }

    protected isActiveComponent(): boolean {
        return this.focusManager.currentActive === this.componentId;
    }

    private focusTags(): void {
        const items = this.tagItems.toArray();
        if (this.tagFocusIndex >= 0) {
            if (items[this.tagFocusIndex]) {
                items[this.tagFocusIndex].nativeElement.focus();
            }
        }
    }
    // #endregion
}
